import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlternativeModeSchema,
  ApiClientError,
  type AlternativeMode,
  type ApiClientErrorCode,
  type PaintListItem,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { PaintResultCard } from './PaintResultCard';

/**
 * Container Альтернативы краски (PRD §6 Block 3 Phase 4). См. ADR-0005.
 * Single-component без раздельного Presenter (тривиальный экран без сложных
 * state-комбинаций, ADR-0005 §«Откат» это разрешает). Симметрия с
 * decors-catalog/AlternativesScreen, но без `kind` параметра — paint один
 * домен (ADR-0010 §1).
 *
 * URL: `/paint/:globalId/alternatives?mode=lighter|darker|warmer|cooler`.
 * Container читает globalId/mode → вызывает apiClient.paint.alternatives →
 * отображает 4 карточки через `PaintResultCard` (Phase 3).
 *
 * MSW-handler в Phase 4 Шаг 3 заменяется на real `findAlternatives`
 * LAB-shift логику (вместо Phase 2 same-tone stub).
 */

const MODE_LABELS: Record<AlternativeMode, string> = {
  lighter: 'Светлее',
  darker: 'Темнее',
  warmer: 'Теплее',
  cooler: 'Холоднее',
};

export function AlternativesScreen() {
  const navigate = useNavigate();
  const params = useParams<{ globalId: string }>();
  const [searchParams] = useSearchParams();

  // react-router-dom v7 уже декодирует path params; повторный
  // decodeURIComponent ломается на Global_ID с `%` (URIError).
  const globalId = params.globalId ?? '';
  const modeRaw = searchParams.get('mode');
  const modeParse = AlternativeModeSchema.safeParse(modeRaw);
  const mode: AlternativeMode | null = modeParse.success ? modeParse.data : null;

  const [items, setItems] = useState<readonly PaintListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!mode || !globalId) {
      setError({ code: 'INVALID_INPUT', message: 'Не указан режим альтернатив' });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(undefined);
    let cancelled = false;
    apiClient.paint.alternatives(globalId, mode).then(
      (res) => {
        if (cancelled) return;
        setItems(res.items);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        // Не leak-аем String(err) в UI: non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [globalId, mode]);

  const title = mode ? `${MODE_LABELS[mode]} похожие краски` : 'Альтернативы';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={title} />
      <section className="flex-1 p-4 overflow-y-auto">
        {loading && <p className="text-muted text-[18px] text-center mt-12 m-0">Загружаем…</p>}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
            <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
              Не удалось получить альтернативы.
            </p>
            {error.message && <p className="text-muted text-sm">{error.message}</p>}
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-muted text-[18px] text-center mt-12 m-0">Альтернативы не найдены.</p>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="flex flex-col gap-5">
            {items.map((item) => (
              <PaintResultCard
                key={item.globalId}
                item={item}
                onItemClick={(id) => navigate(`/paint/${encodeURIComponent(id)}`)}
              />
            ))}
          </div>
        )}
      </section>
      <BottomBar />
    </main>
  );
}
